import React, { useEffect, useRef, useState } from 'react';
import SoundManager from '../utils/SoundManager';

/**
 * FALLOUT / CRYO SUBSYSTEM - the sleeper roster panel.
 * Two inverted-header panels side by side (REACTIVATED / DORMANT) with
 * leader-line rows, plus a PREVIOUS readout box. Rendered inside
 * `.fallout-term` so it inherits the green phosphor treatment; its own
 * classes are scoped under `.fo-cryo`.
 */

interface Sleeper {
  id: number;
  name: string;
  thaw: number | null; // null = not thawing, 0-100 = progress
}

const ROSTER = [
  'Patricia Vane', 'Alexander Doyle', 'Laurence Pike', 'Ian Kettleworth',
  'Betty Ashgrove', 'Howard Maclay', 'Stephanie Orr', 'Elmer Stroud',
  'Chase Powell', 'Joan Polinski', 'Thomas Hardigan', 'Victoria Lamb',
  'Chloe Vance', 'Kathryn Copp', 'Christyn Marr', 'Francesca Mahon',
  'Casey Schultz', 'Arthur Wormeley', 'March Sherwood', 'Maximillian Dottino',
  'Ayana Lee', 'Clare Feldman', 'Archibald Sachs', 'Jeanne Wormwood',
  'Guillaume Bourgogne', 'Odile Delacroix', 'Ezra Fallow', 'Marguerite Ward',
];

const INITIAL_REACTIVATED = 7;
const MIN_DORMANT = 8;
const READOUT = [
  'Log Readout .....',
  'Report Generated',
  '',
  'LOCATION ... SECTOR 4B',
  'COOLANT .... NOMINAL',
  'POWER CORE . 91.4%',
  'POD SEALS .. 2 FLAGGED',
];

interface FalloutCryoPanelProps {
  onBack: () => void;
}

const FalloutCryoPanel: React.FC<FalloutCryoPanelProps> = ({ onBack }) => {
  const [reactivated, setReactivated] = useState<Sleeper[]>(() =>
    ROSTER.slice(0, INITIAL_REACTIVATED).map((name, id) => ({ id, name, thaw: null }))
  );
  const [dormant, setDormant] = useState<Sleeper[]>(() =>
    ROSTER.slice(INITIAL_REACTIVATED).map((name, i) => ({ id: INITIAL_REACTIVATED + i, name, thaw: null }))
  );
  const [readoutIdx, setReadoutIdx] = useState(0);
  const dormantRef = useRef<HTMLDivElement>(null);

  const beep = (name: string) => {
    try { SoundManager.playSound(name); } catch { /* no-op */ }
  };

  // Readout box types itself in, then loops quietly.
  useEffect(() => {
    if (readoutIdx >= READOUT.length) return;
    const t = setTimeout(() => setReadoutIdx((i) => i + 1), 260);
    return () => clearTimeout(t);
  }, [readoutIdx]);

  // Thaw cycle: pods wake on their own, one at a time.
  useEffect(() => {
    const tick = setInterval(() => {
      setDormant((prev) => {
        const thawing = prev.find((s) => s.thaw !== null);

        if (thawing) {
          const step = 12 + Math.floor(Math.random() * 16);
          const next = (thawing.thaw ?? 0) + step;
          if (next >= 100) {
            setReactivated((done) => [...done, { ...thawing, thaw: null }].slice(-14));
            beep('command');
            return prev.filter((s) => s.id !== thawing.id);
          }
          return prev.map((s) => (s.id === thawing.id ? { ...s, thaw: next } : s));
        }

        if (prev.length <= MIN_DORMANT) return prev;
        const idx = Math.floor(Math.random() * prev.length);
        beep('keypress');
        return prev.map((s, i) => (i === idx ? { ...s, thaw: 4 } : s));
      });
    }, 1100);
    return () => clearInterval(tick);
  }, []);

  const startThaw = (id: number) => {
    setDormant((prev) => {
      if (prev.some((s) => s.thaw !== null)) return prev;
      beep('keypress');
      return prev.map((s) => (s.id === id ? { ...s, thaw: 8 } : s));
    });
  };

  const leader = (ch: string) => (
    <span className="fo-lead" aria-hidden="true">{ch.repeat(120)}</span>
  );

  return (
    <div className="fo-cryo">
      <style>{CRYO_CSS}</style>

      <div className="fo-cryo-title">CRYO "SLEEPERS" [58 3-0 320_19 NS2]</div>

      <div className="fo-cryo-grid">
        {/* ---- Left: reactivated + previous readout ---- */}
        <div className="fo-cryo-left">
          <div className="fo-panel">
            <div className="fo-panel-bar">
              <span>REACTIVATED</span>
              <span className="fo-panel-bar-right">Status</span>
            </div>
            <div className="fo-panel-body">
              {reactivated.map((s) => (
                <div className="fo-row" key={s.id}>
                  <span className="fo-row-name">- {s.name}</span>
                  {leader('/')}
                  <span className="fo-row-status">Complete</span>
                </div>
              ))}
            </div>
          </div>

          <div className="fo-prevbox">
            <div className="fo-prev-tab" onClick={onBack}>&lt;- PREVIOUS</div>
            <div className="fo-prev-body">
              {READOUT.slice(0, readoutIdx).map((l, i) => (
                <div className="fo-prev-line" key={i}>{l || ' '}</div>
              ))}
            </div>
          </div>
        </div>

        {/* ---- Right: dormant ---- */}
        <div className="fo-panel fo-panel-tall">
          <div className="fo-panel-head">
            <span className="fo-panel-proj">PJCT CLW270 (GB4K) 29nx</span>
          </div>
          <div className="fo-panel-bar">
            <span>DORMANT</span>
            <span className="fo-panel-bar-right">Status</span>
          </div>
          <div className="fo-panel-body fo-panel-scroll" ref={dormantRef}>
            {dormant.map((s) => (
              <div
                className={`fo-row fo-row-live${s.thaw !== null ? ' fo-row-thaw' : ''}`}
                key={s.id}
                onClick={() => startThaw(s.id)}
              >
                <span className="fo-row-name">- {s.name}</span>
                {leader('_')}
                <span className="fo-row-status">
                  {s.thaw === null ? 'Stable' : `Thawing ${String(Math.min(99, s.thaw)).padStart(2, '0')}%`}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="fo-cryo-foot">
        Click a dormant pod to bring it forward. Backspace returns to the file index.
      </div>
    </div>
  );
};

const CRYO_CSS = `
.fo-cryo {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
  font-size: 0.92em;
}
.fo-cryo-title { text-align: center; letter-spacing: 0.1em; color: var(--fo-hi); }

.fo-cryo-grid {
  flex: 1;
  min-height: 0;
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 12px;
}
.fo-cryo-left { display: flex; flex-direction: column; gap: 12px; min-height: 0; min-width: 0; }

.fo-panel {
  display: flex;
  flex-direction: column;
  min-height: 0;
  min-width: 0;
  border: 2px solid var(--fo);
  box-shadow: 0 0 12px rgba(36,245,106,0.15);
  overflow: hidden;
}
.fo-panel-tall { min-height: 0; }

.fo-panel-head {
  display: flex;
  justify-content: flex-end;
  padding: 2px 6px;
  font-size: 0.9em;
}
.fo-panel-proj { color: var(--fo-hi); letter-spacing: 0.05em; }

.fo-panel-bar {
  display: flex;
  align-items: baseline;
  padding: 1px 6px;
  background: var(--fo);
  color: #021206;
  text-shadow: none;
  font-weight: 700;
  letter-spacing: 0.1em;
  flex-shrink: 0;
}
.fo-panel-bar-right { margin-left: auto; font-weight: 500; letter-spacing: 0.05em; }

.fo-panel-body { padding: 4px 6px; min-width: 0; overflow: hidden; }
.fo-panel-scroll { flex: 1; min-height: 0; overflow-y: auto; }

.fo-row { display: flex; align-items: baseline; gap: 0.4em; min-width: 0; white-space: nowrap; }
.fo-row-live { cursor: pointer; }
.fo-row-live:hover .fo-row-name,
.fo-row-live:hover .fo-row-status { color: var(--fo-hi); }
.fo-row-thaw .fo-row-status { color: var(--fo-hi); animation: fo-cryo-pulse 0.8s ease-in-out infinite; }
.fo-row-name { flex-shrink: 0; }
.fo-row-status { flex-shrink: 0; padding-left: 0.5em; }
.fo-lead { flex: 1 1 0; min-width: 0; overflow: hidden; opacity: 0.55; }

.fo-prevbox {
  flex-shrink: 0;
  min-width: 0;
  border: 2px solid var(--fo);
  display: flex;
  flex-direction: column;
  min-height: 0;
}
.fo-prev-tab {
  padding: 1px 6px;
  letter-spacing: 0.12em;
  border-bottom: 2px solid var(--fo);
  cursor: pointer;
}
.fo-prev-tab:hover { background: var(--fo); color: #021206; text-shadow: none; }
.fo-prev-body { padding: 4px 6px; font-size: 0.9em; }
.fo-prev-line { white-space: pre; }

.fo-cryo-foot { flex-shrink: 0; font-size: 0.85em; color: var(--fo-lo); }

@keyframes fo-cryo-pulse { 0%,100% { opacity: 1; } 50% { opacity: 0.45; } }

@media (max-width: 700px) {
  .fo-cryo-grid { grid-template-columns: 1fr; grid-template-rows: auto 1fr; overflow-y: auto; }
  .fo-cryo-title { font-size: 0.9em; }
}
`;

export default FalloutCryoPanel;
